import React from "react";
import { NavLink } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export interface NavItem {
  label?: string;
  icon?: React.ReactNode;
  to?: string;
  onClick?: () => void;
  section?: string;
  badge?: string | number;
}

interface SideNavigationProps {
  items: NavItem[];
  header?: React.ReactNode;
  side?: "left" | "right";
  width?: number;
}

export default function SideNavigation({
  items,
  header,
  side = "left",
  width = 280,
}: SideNavigationProps) {
  return (
    <aside
      className="
        fixed top-0 z-50 h-screen
        bg-white/85 dark:bg-[#0b0b0b]/95
        backdrop-blur-2xl
        border-r border-gray-200/60 dark:border-gray-800
        shadow-2xl
        flex flex-col
      "
      style={{
        width,
        [side]: 0,
      }}
    >
      {/* ======================================
          HEADER
      ====================================== */}

      {header && (
        <div
          className="
            sticky top-0 z-10
            px-5 py-5
            border-b border-gray-200/70 dark:border-gray-800
            bg-white/70 dark:bg-black/40
            backdrop-blur-xl
          "
        >
          {header}
        </div>
      )}

      {/* ======================================
          NAVIGATION
      ====================================== */}

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
        {items.map((item, index) => {
          // ======================================
          // SECTION
          // ======================================

          if (item.section) {
            return (
              <div
                key={`section-${index}`}
                className="
                  px-3 pt-5 pb-2
                  text-[11px]
                  uppercase
                  tracking-[0.18em]
                  font-bold
                  text-gray-400 dark:text-gray-500
                "
              >
                {item.section}
              </div>
            );
          }

          // ======================================
          // LINK
          // ======================================

          if (item.to) {
            return (
              <NavLink
                key={index}
                to={item.to}
                onClick={item.onClick}
                className={({ isActive }) =>
                  `
                    group relative
                    flex items-center gap-4
                    px-4 py-3.5
                    rounded-2xl
                    transition-all duration-200
                    overflow-hidden
                    ${
                      isActive
                        ? `
                          bg-gradient-to-r
                          from-cyan-500
                          to-sky-500
                          text-white
                          shadow-lg shadow-cyan-500/20
                        `
                        : `
                          text-gray-700 dark:text-gray-200
                          hover:bg-gray-100/80
                          dark:hover:bg-gray-800/70
                        `
                    }
                  `
                }
              >
                {({ isActive }) => (
                  <>
                    {/* ICON */}
                    {item.icon && (
                      <div
                        className={`
                          text-xl shrink-0 transition
                          ${
                            isActive
                              ? "text-white"
                              : "text-gray-500 dark:text-gray-400 group-hover:text-cyan-500"
                          }
                        `}
                      >
                        {item.icon}
                      </div>
                    )}

                    {/* LABEL */}
                    <div className="flex-1 flex items-center justify-between">
                      <span className="font-medium text-[15px]">
                        {item.label}
                      </span>

                      <div className="flex items-center gap-2">
                        {/* BADGE */}
                        {item.badge && (
                          <span
                            className={`
                              text-xs px-2 py-1 rounded-full font-semibold
                              ${
                                isActive
                                  ? "bg-white/20 text-white"
                                  : "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400"
                              }
                            `}
                          >
                            {item.badge}
                          </span>
                        )}

                        {/* ARROW */}
                        <ChevronRight
                          size={16}
                          className={`
                            transition-transform duration-200
                            ${
                              isActive
                                ? "text-white"
                                : "text-gray-400 group-hover:translate-x-1"
                            }
                          `}
                        />
                      </div>
                    </div>

                    {/* ACTIVE GLOW */}
                    {isActive && (
                      <div className="absolute inset-0 rounded-2xl ring-1 ring-white/20" />
                    )}
                  </>
                )}
              </NavLink>
            );
          }

          // ======================================
          // BUTTON
          // ======================================

          return (
            <button
              key={index}
              onClick={item.onClick}
              className="
                group w-full
                flex items-center gap-4
                px-4 py-3.5
                rounded-2xl
                text-left
                text-gray-700 dark:text-gray-200
                hover:bg-gray-100/80
                dark:hover:bg-gray-800/70
                transition-all duration-200
              "
            >
              {/* ICON */}
              {item.icon && (
                <div className="text-xl text-gray-500 dark:text-gray-400 group-hover:text-cyan-500 transition">
                  {item.icon}
                </div>
              )}

              {/* LABEL */}
              <div className="flex-1 flex items-center justify-between">
                <span className="font-medium text-[15px]">
                  {item.label}
                </span>

                <ChevronRight
                  size={16}
                  className="text-gray-400 group-hover:translate-x-1 transition-transform"
                />
              </div>
            </button>
          );
        })}
      </nav>

      {/* ======================================
          FOOTER
      ====================================== */}

      <div
        className="
          border-t border-gray-200/70 dark:border-gray-800
          p-4
          bg-white/50 dark:bg-black/20
          backdrop-blur-xl
        "
      >
        <div className="rounded-2xl bg-gradient-to-r from-cyan-500 to-sky-500 p-4 text-white shadow-lg">
          <div className="text-sm font-semibold">
            Explore Maldives
          </div>

          <div className="text-xs text-white/80 mt-1 leading-relaxed">
            Discover resorts, tours, boats and unforgettable island experiences.
          </div>
        </div>
      </div>
    </aside>
  );
}